import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Wallet, ChevronRight, ChevronDown, MoreHorizontal,
  Phone, Mail, AlertTriangle, ArrowUpRight,
  CheckCircle2, CalendarPlus, XCircle, RotateCw, GitBranch, Loader2,
  Send, Download, PenLine,
} from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import BookFutureNeedDialog from "@/components/checkout/BookFutureNeedDialog";
import { buildBookableVisitRows, resolveBookingDurationMinutes } from "@/lib/planVisits";
import { buildTxPlanPaymentSchedule } from "@/lib/txPlanPaymentSchedule";
import { formatDoctorName } from "@/lib/doctorName";
import { prewarmEmailBackend } from "@/lib/apiClient";
import {
  checkUnassignedBlock,
  clinicSettingsWithLogo,
  downloadTreatmentPlanPdf,
  emailTreatmentPlanPdf,
} from "@/lib/treatmentPlanDelivery";
import MarkLostDialog from "./MarkLostDialog";
import MarkAcceptedDialog from "@/components/treatment/MarkAcceptedDialog";
import SelectVisitProceduresDialog from "./SelectVisitProceduresDialog";
import AmendVisitOutcomeDialog from "@/components/treatment/AmendVisitOutcomeDialog";
import CollectPaymentDialog from "@/components/treatment/CollectPaymentDialog";
import ArPaymentInvoicePrint from "./ArPaymentInvoicePrint";
import SignatureDialog from "@/components/documents/SignatureDialog";
import { resolvePhaseCadenceForItems } from "@/lib/txPlanPhaseCadence";
import {
  getTreatmentPlanDocumentStatus,
  signTreatmentPlanDocument,
  signatureForPdf,
} from "@/lib/signedDocuments";
import {
  STATE_BY_KEY, formatMoney,
} from "./pipelineHelpers";
import {
  planAnswer, groupVisitPhases, folderSummaries, formatRelative,
} from "./planAnswer";

const AR_PAYMENT_METHODS = [
  { id: "cash",          label: "Cash" },
  { id: "gcash",         label: "GCash" },
  { id: "maya",          label: "Maya" },
  { id: "bank_transfer", label: "Bank transfer" },
  { id: "card",          label: "Card" },
  { id: "check",         label: "Check" },
];

// =====================================================================
// Tx Plan Drawer — drill-down for one plan
// =====================================================================
// Slides in from the right. Open/close state is the URL param `?plan=<uuid>`
// so it's refresh-safe and shareable.
//
// Layout is answer-first, because front desk opens this with a patient
// standing there and needs one of three answers: does he owe us money,
// when is he coming back, what did we already do.
//
//   1. One sentence at the top — money state, then what happens next.
//   2. The single primary action directly under it.
//   3. Three collapsed folders — Treatment, Payments, History — each
//      showing its own summary line while shut, so a closed folder still
//      carries its number. Everything the old six-panel report showed
//      still lives inside them.
//
// Secondary actions (resend, PDF, sign, mark lost, reconfirm) sit behind
// the footer's More menu so the destructive one is never a mis-tap away
// from the button front desk presses all day.
// =====================================================================

export default function TxPlanDrawer({ planId, currency = "PHP", onClose, onPlanChanged }) {
  const open = !!planId;
  const { activeClinic } = useAuth();

  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [clinicForPdf, setClinicForPdf] = useState(null);
  const [proposalBusy, setProposalBusy] = useState({
    propose: false,
    resend: false,
    download: false,
  });
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [lostOpen, setLostOpen]         = useState(false);
  const [reconfirming, setReconfirming] = useState(false);
  // Mark Accepted modal — opened from the action footer when the
  // plan is in 'proposed' state. Stays inside Tx Plan Center; never
  // navigates to the patient or doctor view.
  const [markAcceptedOpen, setMarkAcceptedOpen] = useState(false);
  // Plan + items hydrated when MarkAcceptedDialog opens (drawer's
  // own payload from tx_plan_center_drawer doesn't include items).
  const [planForAccept, setPlanForAccept] = useState(null);
  // Multi-procedure selection prompt. Opens before the slot picker
  // whenever 2+ Accepted items are waiting for a booking. Holds the
  // ids the receptionist confirmed for THIS appointment so handleBooked
  // can loop and call schedule_plan_item once per item.
  const [selectOpen, setSelectOpen]       = useState(false);
  const [pendingItemIds, setPendingItemIds] = useState([]);
  // PR-F: amend-outcome dialog state. Holds the procedure_visit row
  // being amended (or null when closed). Wires onto PR-E's RPC.
  const [amendVisit, setAmendVisit] = useState(null);
  const [collectArOpen, setCollectArOpen] = useState(false);
  const [arInvoice, setArInvoice] = useState(null);
  const [signatureDocument, setSignatureDocument] = useState(null);
  const [signingOpen, setSigningOpen] = useState(false);
  const [signatureBusy, setSignatureBusy] = useState(false);

  const refreshSignatureDocument = useCallback(async () => {
    if (!planId) {
      setSignatureDocument(null);
      return null;
    }
    try {
      const doc = await getTreatmentPlanDocumentStatus({ planId });
      setSignatureDocument(doc || null);
      return doc || null;
    } catch (err) {
      console.warn("[tx-plan-center] signature status failed", err);
      setSignatureDocument(null);
      return null;
    }
  }, [planId]);

  const fetchData = useCallback(async () => {
    if (!planId) return;
    setLoading(true);
    setError(null);
    // Pull the drawer envelope, full plan (with items), and canonical
    // billing state in parallel. Checkout also reads
    // get_plan_billing_state; using it here keeps Tx Plan Center 1:1
    // with checkout instead of relying on a second balance calculator.
    // tx_plan_center_drawer doesn't include items in its payload, but
    // we need them downstream so eligibleItem (the
    // "Book Visit 1" target) can find a Scheduled-eligible row.
    const [drawerRes, planRes, billingRes, apptSettingsRes, clinicSettingsRes] = await Promise.all([
      supabase.rpc("tx_plan_center_drawer", { p_plan_id: planId }),
      supabase.rpc("get_treatment_plan",   { p_plan_id: planId }),
      supabase.rpc("get_plan_billing_state", { p_plan_id: planId }),
      supabase.rpc("get_appointment_settings"),
      supabase.rpc("get_clinic_settings"),
    ]);
    if (drawerRes.error) {
      setError(drawerRes.error);
      setLoading(false);
      return;
    }
    // Merge items from get_treatment_plan into the drawer's plan
    // object. If the items fetch fails we still render the drawer;
    // booking just won't be possible until the next refresh.
    // appointment_settings carries chair_setup_minutes, used by the
    // booking bundle to compute real chair time (not a 60-min heuristic).
    const merged = drawerRes.data ? {
      ...drawerRes.data,
      plan: {
        ...(drawerRes.data.plan || {}),
        items: planRes.error ? [] : (planRes.data?.items ?? []),
        // phase_meta carries the dentist's typed per-appointment durations
        // so booking honors "Appointment 1 → 15 min" over raw chair time.
        phase_meta: planRes.error ? {} : (planRes.data?.phase_meta ?? {}),
      },
      billing_state: billingRes.error ? null : billingRes.data,
      appointment_settings: apptSettingsRes.error ? null : apptSettingsRes.data,
    } : null;
    setData(merged);
    if (!clinicSettingsRes.error && clinicSettingsRes.data) {
      setClinicForPdf(await clinicSettingsWithLogo(clinicSettingsRes.data, activeClinic));
    } else {
      setClinicForPdf(activeClinic || null);
    }
    setLoading(false);
    await refreshSignatureDocument();
  }, [planId, activeClinic, refreshSignatureDocument]);

  // Re-fetch whenever a different plan opens
  useEffect(() => {
    if (!planId) {
      setSignatureDocument(null);
      return;
    }
    let cancelled = false;
    setData(null);
    fetchData().catch(() => { /* surfaced via setError already */ });
    return () => { cancelled = true; };
  }, [planId, fetchData]);

  useEffect(() => { prewarmEmailBackend(); }, []);

  // Every visit/stage eligible for booking. The scheduling unit is
  // procedure_visits, not the parent plan item: a crown can have V1 in
  // Appointment 1, V2 in Appointment 2, and V3 in Appointment 3.
  // Receptionist picks which stage rows go into the next appointment.
  const eligibleItems = useMemo(() => {
    return buildBookableVisitRows(data?.plan?.items || []);
  }, [data]);
  const eligibleItem = eligibleItems[0] || null;

  // Most-recent completed procedure_visit on this plan. Used to anchor
  // the booking modal's smart-default date so visit 2+ defaults to
  // "previous-visit + gap" instead of "today + gap" — the natural
  // multi-visit booking model (book one, complete it at checkout, book
  // the next).
  const lastCompletedVisitAt = useMemo(() => {
    const visits = data?.visits || [];
    let latest = null;
    for (const v of visits) {
      if (!v.completed_at) continue;
      if (!latest || new Date(v.completed_at) > new Date(latest)) {
        latest = v.completed_at;
      }
    }
    return latest;
  }, [data]);

  const arBalanceCentavos = useMemo(() => {
    const billing = data?.billing_state;
    if (billing) return Number(billing.remaining_centavos ?? 0);
    return Number(data?.balance?.balance_centavos ?? 0);
  }, [data]);

  // Built once here rather than inside the Payments folder, because the
  // answer sentence needs it too (an overdue installment is what turns
  // "Owes ₱12,500." into "Owes ₱12,500, overdue."). One calculation, two
  // readers — the sentence can't disagree with the table below it.
  const paymentSchedule = useMemo(() => {
    if (!data) return null;
    return buildTxPlanPaymentSchedule({
      billingState: data.billing_state,
      visits: data.visits || [],
      items: data.plan?.items || [],
    });
  }, [data]);

  const answer = useMemo(
    () => planAnswer({ data, schedule: paymentSchedule, currency }),
    [data, paymentSchedule, currency],
  );

  const summaries = useMemo(
    () => folderSummaries({ data, currency }),
    [data, currency],
  );

  // Resolve the visit/stage rows the receptionist confirmed for this booking
  // (set by SelectVisitProceduresDialog). Falls back to the single
  // eligibleItem when only one is unscheduled — that case skips the
  // prompt entirely.
  const pendingItems = useMemo(() => {
    const ids = new Set(pendingItemIds);
    return eligibleItems.filter((i) => ids.has(i.id));
  }, [eligibleItems, pendingItemIds]);

  // Derive a "bundle" shape that BookFutureNeedDialog (the canonical
  // popup-booking pattern from Checkout's Step 3) can consume. Carries
  // the FULL set of selected visit rows so a multi-procedure
  // appointment books once and links every procedure_visit below.
  //
  // Duration comes from the selected procedure_visits.chair_min rows.
  // This is the same raw appointment-lane number shown in the Tx Plan
  // board, so a 165-minute Appointment 1 opens a 165-minute slot block.
  const bookBundle = useMemo(() => {
    if (pendingItems.length === 0) return null;
    const lead = pendingItems[0];
    const categories = Array.from(new Set(
      pendingItems.map((it) => it.procedure_category).filter(Boolean),
    ));
    const isMulti = pendingItems.length > 1;
    const realMinutes = resolveBookingDurationMinutes(pendingItems, data?.plan?.phase_meta);
    const fallbackMinutes = 60 + Math.max(0, pendingItems.length - 1) * 30;
    return {
      procedure_name:   isMulti ? `${pendingItems.length} stages` : (lead.display_label || lead.procedure_name),
      tooth_number:     isMulti ? null : lead.tooth_number,
      surfaces:         isMulti ? [] : (lead.surface ? [lead.surface] : []),
      needs:            pendingItems,
      estimatedMinutes: realMinutes > 0 ? realMinutes : fallbackMinutes,
      category:         lead.procedure_category || null,
      categories,
      isAll:            isMulti,
    };
  }, [pendingItems, data?.plan?.phase_meta]);

  const phaseCadence = useMemo(() => {
    if (pendingItems.length === 0) return null;
    return resolvePhaseCadenceForItems({
      plan: data?.plan,
      visits: data?.visits || [],
      selectedItems: pendingItems,
    });
  }, [data?.plan, data?.visits, pendingItems]);

  // After BookFutureNeedDialog creates the (Scheduled) appointment, we
  // link EVERY confirmed procedure_visit to it via schedule_procedure_visit
  // (one RPC per stage), then refresh both the drawer and the kanban so the
  // visit shows up. Errors are aggregated — if one link fails the
  // appointment still exists and the successful items are linked.
  const handleBooked = useCallback(async (apptId /*, label */) => {
    setScheduleOpen(false);
    const targets = pendingItems;
    if (!apptId || targets.length === 0) {
      // Booking succeeded but we lost the linkage context — refresh
      // anyway so the visit at least surfaces, even if it's not
      // attached to any plan item yet. The receptionist can attach
      // missing items from the patient chart's Treatment tab as a
      // backstop.
      setPendingItemIds([]);
      fetchData();
      onPlanChanged?.();
      return;
    }
    let okCount = 0;
    const failures = [];
    for (const t of targets) {
      const visitId = t.visit_id || t.procedure_visit_id || t.id;
      const { error: linkErr } = await supabase.rpc("schedule_procedure_visit", {
        p_visit_id:       visitId,
        p_appointment_id: apptId,
      });
      if (linkErr) {
        failures.push({ item: t, message: linkErr.message ?? "Unknown error." });
      } else {
        okCount += 1;
      }
    }
    setPendingItemIds([]);
    if (failures.length === 0) {
      toast.success(targets.length === 1
        ? "Visit booked and linked."
        : `Visit booked. ${okCount} procedures linked.`);
    } else {
      const head = failures[0];
      toast.error(
        `${okCount} linked, ${failures.length} failed. First: ${head.message}`,
      );
    }
    fetchData();
    onPlanChanged?.();
  }, [pendingItems, fetchData, onPlanChanged]);

  // Action-button entry point. If only one item is waiting, skip the
  // prompt and go straight to the slot picker. Two or more → open the
  // selection dialog first so every procedure is explicitly handled
  // (no Array.find silent-drop).
  const handleStartBooking = useCallback(() => {
    if (eligibleItems.length === 0) return;
    if (eligibleItems.length === 1) {
      setPendingItemIds([eligibleItems[0].id]);
      setScheduleOpen(true);
      return;
    }
    setSelectOpen(true);
  }, [eligibleItems]);

  // Receptionist confirmed which items go into this booking — chain
  // straight into BookFutureNeedDialog with the chosen items.
  const handleSelectionConfirmed = useCallback((ids) => {
    if (!ids || ids.length === 0) return;
    setPendingItemIds(ids);
    setSelectOpen(false);
    setScheduleOpen(true);
  }, []);

  const handleMarkedLost = useCallback(() => {
    setLostOpen(false);
    fetchData();
    onPlanChanged?.();
  }, [fetchData, onPlanChanged]);

  // Open the Mark Accepted modal for the plan currently shown in the
  // drawer. Hydrates plan + items via get_treatment_plan because the
  // tx_plan_center_drawer payload doesn't include items (the dialog
  // needs them to compute the visit-1 count + plan total preview).
  const openMarkAccepted = useCallback(async () => {
    if (!planId) return;
    const { data: full, error: err } = await supabase.rpc("get_treatment_plan", {
      p_plan_id: planId,
    });
    if (err) {
      toast.error(err.message ?? "Could not load plan items.");
      return;
    }
    setPlanForAccept(full ?? { id: planId, items: [] });
    setMarkAcceptedOpen(true);
  }, [planId]);

  const loadPatientFacingPlan = useCallback(async () => {
    if (!planId) return null;
    const { data: full, error: err } = await supabase.rpc("get_treatment_plan", {
      p_plan_id: planId,
    });
    if (err) {
      toast.error(err.message ?? "Could not load plan.");
      return null;
    }
    const block = checkUnassignedBlock(full);
    if (block) {
      toast.error(block);
      return null;
    }
    return full;
  }, [planId]);

  const handleDownloadPdf = useCallback(async () => {
    setProposalBusy((b) => ({ ...b, download: true }));
    const full = await loadPatientFacingPlan();
    if (full) {
      const signed = await refreshSignatureDocument();
      await downloadTreatmentPlanPdf({
        planData: full,
        patient: data?.patient,
        clinicForPdf,
        activeClinic,
        signature: signatureForPdf(signed),
        toast,
      });
    }
    setProposalBusy((b) => ({ ...b, download: false }));
  }, [activeClinic, clinicForPdf, data?.patient, loadPatientFacingPlan, refreshSignatureDocument]);

  const handleResendEmail = useCallback(async () => {
    setProposalBusy((b) => ({ ...b, resend: true }));
    const full = await loadPatientFacingPlan();
    if (full) {
      const signed = await refreshSignatureDocument();
      await emailTreatmentPlanPdf({
        planData: full,
        patient: data?.patient,
        clinicForPdf,
        activeClinic,
        signature: signatureForPdf(signed),
        successMessage: "Plan resent to patient.",
        toast,
      });
    }
    setProposalBusy((b) => ({ ...b, resend: false }));
  }, [activeClinic, clinicForPdf, data?.patient, loadPatientFacingPlan, refreshSignatureDocument]);

  const handleSignatureSubmit = useCallback(async (signature) => {
    if (!planId) return;
    setSignatureBusy(true);
    try {
      const acceptPlan = data?.plan?.status === "Proposed";
      const result = await signTreatmentPlanDocument({
        planId,
        signature,
        acceptPlan,
      });
      setSignatureDocument(result || null);
      setSigningOpen(false);
      toast.success(acceptPlan ? "Plan signed and accepted." : "Document signed.");
      await fetchData();
      onPlanChanged?.();
    } catch (err) {
      toast.error(err?.message ?? "Could not save signature.");
      console.warn("[tx-plan-center] signature save failed", err);
    } finally {
      setSignatureBusy(false);
    }
  }, [data?.plan?.status, fetchData, onPlanChanged, planId]);

  const handlePropose = useCallback(async () => {
    if (!planId) return;
    setProposalBusy((b) => ({ ...b, propose: true }));
    const preflight = await loadPatientFacingPlan();
    if (!preflight) {
      setProposalBusy((b) => ({ ...b, propose: false }));
      return;
    }
    const { error: rpcErr } = await supabase.rpc("propose_treatment_plan", { p_plan_id: planId });
    if (rpcErr) {
      setProposalBusy((b) => ({ ...b, propose: false }));
      toast.error(rpcErr.message ?? "Could not propose plan.");
      return;
    }
    const { data: proposedPlan, error: reloadErr } = await supabase.rpc("get_treatment_plan", {
      p_plan_id: planId,
    });
    if (reloadErr) {
      toast.error(reloadErr.message ?? "Plan was proposed, but could not reload for email.");
    } else {
      await emailTreatmentPlanPdf({
        planData: proposedPlan ?? preflight,
        patient: data?.patient,
        clinicForPdf,
        activeClinic,
        successMessage: "Plan proposed and emailed to patient.",
        toast,
      });
    }
    setProposalBusy((b) => ({ ...b, propose: false }));
    await fetchData();
    onPlanChanged?.();
  }, [activeClinic, clinicForPdf, data?.patient, fetchData, loadPatientFacingPlan, onPlanChanged, planId]);

  // After accept_plan succeeds:
  //   path='schedule' → chain into the booking flow so the receptionist
  //                     picks Visit 1 inline. We re-fetch first so the
  //                     freshly-accepted items are visible to the
  //                     selection prompt; otherwise eligibleItems is
  //                     empty and the prompt would no-op.
  //   path='queue'    → just refresh; plan now sits in Needs Booking.
  // Either way, we never navigate away from Tx Plan Center.
  const handleAccepted = useCallback(async (result) => {
    setMarkAcceptedOpen(false);
    setPlanForAccept(null);
    await fetchData();
    onPlanChanged?.();
    if (result?.path === "schedule") {
      // Defer one tick so the freshly-fetched items have committed
      // into local state before handleStartBooking reads eligibleItems.
      setTimeout(() => handleStartBooking(), 0);
    }
  }, [fetchData, onPlanChanged, handleStartBooking]);

  const handleReconfirm = useCallback(async () => {
    if (!planId) return;
    setReconfirming(true);
    const { data: result, error: err } = await supabase.rpc("reconfirm_plan", {
      p_plan_id: planId,
    });
    setReconfirming(false);
    if (err) {
      toast.error(err.message ?? "Could not reconfirm plan.");
      return;
    }
    toast.success("Plan reconfirmed. Opening the new draft.");
    onPlanChanged?.();
    // Swap drawer to the new plan via URL change; the parent owns
    // ?plan=<id>, so just trigger a navigation by closing+reopening.
    if (result?.new_plan_id) {
      // We don't have direct access to setSearchParams here, so emit
      // a dom event the parent listens to.
      window.dispatchEvent(new CustomEvent("dos:tx-plan-center-open", {
        detail: { planId: result.new_plan_id },
      }));
    }
  }, [planId, onPlanChanged]);

  const handleArCollected = useCallback(async (result) => {
    const invoicePayload = {
      result,
      paymentEventId: result?.payment_event_id,
      payment: result?.payment,
      balance: result?.balance,
      patient: data?.patient,
      plan: data?.plan,
      planCode: data?.plan_code,
      paidAt: new Date().toISOString(),
    };
    setArInvoice(invoicePayload);
    setTimeout(() => window.print(), 80);
    await fetchData();
    onPlanChanged?.();
  }, [data, fetchData, onPlanChanged]);

  // One primary action, everything else behind the footer's More menu.
  // Same per-state action sets as before — only the ranking is new.
  // Declared after the handlers above because it closes over every one.
  const actions = useMemo(() => buildDrawerActions({
    data,
    eligibleItem,
    eligibleCount: eligibleItems.length,
    arBalanceCentavos,
    proposalBusy,
    reconfirming,
    signatureDocument,
    signatureBusy,
    onBookVisit: handleStartBooking,
    onMarkLost: () => setLostOpen(true),
    onReconfirm: handleReconfirm,
    onMarkAccepted: openMarkAccepted,
    onCollectAr: () => setCollectArOpen(true),
    onPropose: handlePropose,
    onResendEmail: handleResendEmail,
    onDownloadPdf: handleDownloadPdf,
    onSignDocument: () => setSigningOpen(true),
  }), [
    data, eligibleItem, eligibleItems.length, arBalanceCentavos, proposalBusy,
    reconfirming, signatureDocument, signatureBusy, handleStartBooking,
    handleReconfirm, openMarkAccepted, handlePropose, handleResendEmail,
    handleDownloadPdf,
  ]);

  return (
    <Sheet open={open} onOpenChange={(next) => { if (!next) onClose?.(); }}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[640px] p-0 flex flex-col gap-0 overflow-hidden"
      >
        <DrawerHeader data={data} loading={loading} />
        <div className="flex-1 overflow-y-auto bg-slate-50">
          {error ? (
            <ErrorBanner error={error} />
          ) : loading || !data ? (
            <SkeletonBody />
          ) : (
            <>
              <AnswerBlock answer={answer} action={actions.primary} />
              {/* Always visible. This is the spine of the plan — "when is he
                  coming back" and "what does he owe on that visit" answered on
                  one row, so nothing is listed twice and the panel is never
                  empty on open. */}
              <VisitTimeline
                visits={data.visits}
                items={data.plan?.items}
                schedule={paymentSchedule}
                currency={currency}
                onAmendVisit={setAmendVisit}
              />
              <div className="p-4 pt-3 space-y-2">
                <Folder
                  id="treatment"
                  title="Treatment"
                  summary={summaries.treatment}
                >
                  <PlanStructurePanel plan={data.plan} currency={currency} />
                  <DoctorDriftNote patient={data.patient} planDoctor={data.doctor} />
                </Folder>

                <Folder
                  id="payments"
                  title="Payments"
                  summary={summaries.payments}
                >
                  <PlanMoneyPanel
                    billingState={data.billing_state}
                    balance={data.balance}
                    plan={data.plan}
                    schedule={paymentSchedule}
                    currency={currency}
                  />
                </Folder>

                <Folder
                  id="history"
                  title="History"
                  summary={summaries.history}
                >
                  <ActivityLogPanel entries={data.activity} patient={data.patient} />
                </Folder>
              </div>
            </>
          )}
        </div>
        {!error && data ? (
          <DrawerFooter
            secondary={actions.secondary}
            patientId={data.patient?.id}
            planId={data.plan?.id}
            isOperationalMv={!!data.plan?.is_informal}
            onClose={onClose}
          />
        ) : null}

        {/* Multi-procedure selection prompt — opens before the slot
            picker whenever 2+ Accepted items are waiting on a booking.
            Smart-default selection is the lowest-phase group. The
            receptionist can adjust to control which procedures go into
            this appointment vs. stay on the plan for a later visit. */}
        <SelectVisitProceduresDialog
          open={selectOpen}
          onOpenChange={setSelectOpen}
          items={eligibleItems}
          currency={currency}
          patientName={data?.patient?.full_name}
          planCode={data?.plan_code}
          planIsBundle={!!data?.plan?.is_informal}
          onConfirm={handleSelectionConfirmed}
        />

        {/* Canonical popup-booking pattern from Checkout Step 3.
            appointmentStatus='Scheduled' makes it a permanent booking
            on click (vs Checkout's transient 'Pencil' that promotes
            at commit). priorVisitDate anchors the smart-default to
            the last completed visit so visit 2+ default to
            "previous-visit + gap" instead of "today + gap". After
            onBooked fires, handleBooked links the new appointment to
            the plan item via schedule_plan_item. */}
        <BookFutureNeedDialog
          open={scheduleOpen}
          onOpenChange={(next) => {
            setScheduleOpen(next);
            // User dismissed the slot picker without booking — clear
            // the pending selection so the next click re-opens the
            // selection prompt instead of replaying a stale set.
            if (!next) setPendingItemIds([]);
          }}
          bundle={bookBundle}
          patientId={data?.patient?.id}
          patientName={data?.patient?.full_name}
          defaultDoctorId={data?.doctor?.id || null}
          defaultChairId={null}
          appointmentStatus="Scheduled"
          priorVisitDate={phaseCadence?.anchorDate || lastCompletedVisitAt}
          doctorRequestedDays={phaseCadence?.offsetDays ?? null}
          hideHintForVisitOne={true}
          headingOverride="Book plan appointment"
          subtitleOverride={bookBundle?.procedure_name || "Plan visit"}
          onBooked={handleBooked}
        />

        <MarkLostDialog
          open={lostOpen}
          onOpenChange={setLostOpen}
          planId={data?.plan?.id}
          planCode={data?.plan_code}
          patientName={data?.patient?.full_name}
          onMarked={handleMarkedLost}
        />

        <CollectPaymentDialog
          open={collectArOpen}
          onOpenChange={setCollectArOpen}
          planId={data?.plan?.id}
          balanceCentavos={Math.max(0, arBalanceCentavos)}
          label="Collect AR Payment"
          balanceLabel="AR balance"
          successMessage="AR payment collected. Opening invoice print."
          paymentMethods={AR_PAYMENT_METHODS}
          onCollected={handleArCollected}
        />

        <ArPaymentInvoicePrint
          invoice={arInvoice}
          currency={currency}
        />

        {/* Lifecycle v2 acceptance — hosted inside the drawer so the
            receptionist never leaves Tx Plan Center. Two paths land in
            handleAccepted: 'schedule' chains into the booking dialog
            above; 'queue' just refreshes and the plan moves to
            Needs Booking. */}
        <MarkAcceptedDialog
          open={markAcceptedOpen}
          onOpenChange={(v) => {
            setMarkAcceptedOpen(v);
            if (!v) setPlanForAccept(null);
          }}
          plan={planForAccept}
          patientId={data?.patient?.id ?? planForAccept?.patient_id ?? null}
          onAccepted={handleAccepted}
        />

        <SignatureDialog
          open={signingOpen}
          onOpenChange={(next) => {
            if (!next && !signatureBusy) setSigningOpen(false);
          }}
          context={{
            title: data?.plan?.status === "Proposed"
              ? "Sign and accept treatment plan"
              : "Sign treatment plan",
            documentNumber: data?.plan_code || data?.plan?.plan_code || data?.plan?.id,
            patientName: patientDisplayName(data?.patient),
          }}
          defaultSignerName={patientDisplayName(data?.patient)}
          busy={signatureBusy}
          onSubmit={handleSignatureSubmit}
        />

        {/* PR-F: amend a past visit's outcome retroactively. Wires onto
            dos_v2.amend_visit_outcome (PR-E). On success we refresh
            both the drawer (so the visit timeline reflects the new state)
            and the parent kanban (the plan may flip to Completed). */}
        <AmendVisitOutcomeDialog
          open={!!amendVisit}
          onOpenChange={(o) => { if (!o) setAmendVisit(null); }}
          visit={amendVisit}
          patientName={data?.patient
            ? [data.patient.first_name, data.patient.last_name].filter(Boolean).join(" ")
            : null}
          onAmended={() => {
            setAmendVisit(null);
            fetchData();
            onPlanChanged?.();
          }}
        />
      </SheetContent>
    </Sheet>
  );
}

// ---------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------
function DrawerHeader({ data, loading }) {
  const stateKey = data?.pipeline_state || "abandoned";
  const state = STATE_BY_KEY[stateKey] ?? STATE_BY_KEY.abandoned;
  const StateIcon = state.Icon;
  const patientName = patientDisplayName(data?.patient);
  // Prefer the real plan_code from PR 3 schema; fall back to UUID prefix
  // for any pre-PR-3 plan that hasn't been backfilled yet.
  const planCode = data?.plan_code
    || (data?.plan?.id ? "TP-" + data.plan.id.slice(0, 6).toUpperCase().replace(/-/g, "") : null);
  // Note: no custom close button here — shadcn's <SheetContent> already
  // renders one in the top-right via its built-in <SheetClose>. Adding
  // our own caused a stacked double-X.
  const phone = data?.patient?.phone || null;
  const email = data?.patient?.email || null;
  return (
    <SheetHeader className="px-4 py-3 pr-12 border-b border-slate-200 bg-white space-y-0">
      <div className="min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <SheetTitle className="text-[16px] font-semibold text-slate-900 truncate">
            {loading ? "Loading…" : patientName}
          </SheetTitle>
          {/* The one thing from the old Patient panel front desk actually
              needs when the patient is NOT standing there: a way to reach
              them to book. One tap, no rows to read. */}
          {phone ? (
            <a
              href={`tel:${phone}`}
              title={`Call ${phone}`}
              aria-label={`Call ${phone}`}
              data-testid="drawer-call-patient"
              className="shrink-0 p-1 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100"
            >
              <Phone className="w-3.5 h-3.5" strokeWidth={2.25} />
            </a>
          ) : null}
          {email ? (
            <a
              href={`mailto:${email}`}
              title={`Email ${email}`}
              aria-label={`Email ${email}`}
              data-testid="drawer-email-patient"
              className="shrink-0 p-1 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100"
            >
              <Mail className="w-3.5 h-3.5" strokeWidth={2.25} />
            </a>
          ) : null}
        </div>
        <div className="mt-0.5 flex items-center gap-2 text-[12px] text-slate-500 flex-wrap">
          {data?.patient?.patient_number ? (
            <span className="font-mono tracking-tight">{data.patient.patient_number}</span>
          ) : null}
          <span className="text-slate-300">·</span>
          {planCode ? (
            <span className="font-mono tracking-tight font-semibold text-slate-700">{planCode}</span>
          ) : null}
          {data?.parent_plan_code ? (
            <span className="inline-flex items-center gap-1 text-[10px] text-slate-500 font-mono">
              <GitBranch className="w-3 h-3" strokeWidth={2.5} />
              v2 of {data.parent_plan_code}
            </span>
          ) : null}
          <span
            className={[
              "px-1.5 py-0.5 rounded-md text-[10px] font-medium inline-flex items-center gap-1",
              state.pill,
            ].join(" ")}
          >
            <StateIcon className="w-3 h-3" strokeWidth={2.5} />
            {state.label}
          </span>
        </div>
      </div>
    </SheetHeader>
  );
}

function patientDisplayName(patient) {
  if (!patient) return "—";
  const full = String(patient.full_name || "").trim();
  if (full) return full;
  const fromParts = [patient.first_name, patient.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  return fromParts || "—";
}

function ErrorBanner({ error }) {
  return (
    <div className="m-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2.5 text-[13px] text-rose-800 flex items-start gap-2">
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={2} />
      <div>
        <div className="font-semibold">Couldn’t load plan.</div>
        <div className="mt-0.5 text-rose-700 text-[12px]">{String(error.message || error)}</div>
      </div>
    </div>
  );
}

function SkeletonBody() {
  return (
    <div className="p-4 space-y-3">
      {[1,2,3,4].map((i) => (
        <div key={i} className="h-24 rounded-lg border border-slate-200 bg-white animate-pulse" />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------
// The answer — one sentence, then the single action it implies
// ---------------------------------------------------------------------
// Money clause is colored, next clause is not: the eye lands on the
// money first because that's the question asked most. Both clauses stay
// plain sentences — no pills, no abbreviations, nothing to decode.
const ANSWER_TONE = {
  owing:   "text-rose-700",
  settled: "text-emerald-700",
  neutral: "text-slate-700",
};

function AnswerBlock({ answer, action }) {
  if (!answer) return null;
  return (
    <div className="px-4 pt-4 pb-1 bg-white border-b border-slate-200">
      <p className="text-[15px] leading-relaxed text-slate-900" data-testid="tx-plan-answer">
        <span className={["font-semibold", ANSWER_TONE[answer.tone] || ANSWER_TONE.neutral].join(" ")}>
          {answer.money}
        </span>{" "}
        {answer.next}
      </p>
      {action ? (
        <div className="mt-3 pb-3">
          <ActionButton action={action} full />
        </div>
      ) : (
        <div className="pb-3" />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// Folder — a closed section that still shows its number
// ---------------------------------------------------------------------
// Hand-rolled rather than <details> so a shut folder renders nothing at
// all: the payment schedule table is the heaviest thing in the drawer and
// there's no reason to build it until someone asks. aria-expanded +
// aria-controls give it the standard accordion semantics.
//
// Always starts closed. Remembering per-user state was considered and
// rejected — front desk builds muscle memory on a screen that looks the
// same every time they open it.
function Folder({ id, title, summary, children }) {
  const [open, setOpen] = useState(false);
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <section className="rounded-lg border border-slate-200 bg-white overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={`tx-plan-folder-${id}`}
        data-testid={`tx-plan-folder-${id}`}
        className="w-full px-3 py-2.5 flex items-center gap-2 text-left hover:bg-slate-50"
      >
        <Chevron className="w-4 h-4 text-slate-400 shrink-0" strokeWidth={2.25} />
        <span className="text-[13px] font-medium text-slate-900 shrink-0">{title}</span>
        <span className="flex-1 text-[11px] text-slate-500 text-right truncate">
          {summary}
        </span>
      </button>
      {open ? (
        <div
          id={`tx-plan-folder-${id}`}
          className="px-3 pb-3 pt-1 space-y-3 border-t border-slate-100"
        >
          {children}
        </div>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------
// Doctor drift note — lives inside Treatment now
// ---------------------------------------------------------------------
// "Preferred Doctor" comes from patients.preferred_doctor_id, populated by
// first-touch triggers. The plan's own doctor is the one recorded at plan
// creation. They diverge on older plans created before the preferred-doctor
// pin landed (e.g. TP-5EBA83's "Dr. Sample (Demo)" ghost). Surfacing the
// gap is deliberate — this warning is the reason the old Patient panel
// showed both doctors, and it survives the redesign unchanged.
function DoctorDriftNote({ patient, planDoctor }) {
  const preferred = patient?.preferred_doctor;
  const drift = preferred?.display_name
    && planDoctor?.display_name
    && preferred.display_name !== planDoctor.display_name;
  if (!drift) return null;
  return (
    <div className="px-2 py-1.5 rounded-md bg-amber-50 border border-amber-200 text-[11px] text-amber-900 flex items-start gap-1.5">
      <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" strokeWidth={2.25} />
      <span>
        This plan was created with <strong>{formatDoctorName(planDoctor, "—")}</strong> —
        different from the patient&apos;s preferred doctor,{" "}
        <strong>{formatDoctorName(preferred, "—")}</strong>.
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------
// Treatment folder — what is on the plan and what it costs
// ---------------------------------------------------------------------
function PlanStructurePanel({ plan, currency }) {
  const items = plan?.items || [];
  const totalCentavos = items.reduce((acc, it) => {
    if (["Cancelled","Declined"].includes(it.status)) return acc;
    return acc + (it.fee_centavos || 0);
  }, 0);
  // No sub-header: the folder wrapping this already says "Treatment".
  return (
    <div>
      {items.length === 0 ? (
        <div className="text-[12px] text-slate-500 italic">No items on this plan.</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((it) => {
            const dimmed = ["Cancelled","Declined"].includes(it.status);
            return (
              <li
                key={it.id}
                className={[
                  "py-1.5 flex items-baseline justify-between gap-2 text-[12px]",
                  dimmed ? "opacity-50" : "",
                ].join(" ")}
              >
                <div className="min-w-0 flex-1 flex items-baseline gap-2">
                  <span className="text-slate-900 truncate">
                    {it.display_label || it.procedure_name || "—"}
                  </span>
                  {it.tooth_number ? (
                    <span className="text-[10px] text-slate-500 font-mono shrink-0">
                      #{it.tooth_number}{it.surface ? ` · ${it.surface}` : ""}
                    </span>
                  ) : null}
                </div>
                <ItemStatusPill status={it.status} />
                <span className="font-semibold tabular-nums text-slate-900 shrink-0 w-20 text-right">
                  {formatMoney(it.fee_centavos, currency)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between text-[12px]">
        <span className="text-slate-600 font-medium">Plan total</span>
        <span className="font-bold tabular-nums text-slate-900">
          {formatMoney(totalCentavos, currency)}
        </span>
      </div>
    </div>
  );
}

const ITEM_STATUS_STYLES = {
  Candidate: "bg-slate-100 text-slate-600",
  Proposed:  "bg-blue-100 text-blue-800",
  Accepted:  "bg-emerald-100 text-emerald-800",
  Scheduled: "bg-violet-100 text-violet-800",
  Completed: "bg-zinc-100 text-zinc-700",
  Deferred:  "bg-amber-100 text-amber-800",
  Declined:  "bg-rose-100 text-rose-800",
  Cancelled: "bg-zinc-100 text-zinc-500",
};

function ItemStatusPill({ status }) {
  const cls = ITEM_STATUS_STYLES[status] || "bg-slate-100 text-slate-600";
  return (
    <span className={["px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0", cls].join(" ")}>
      {status}
    </span>
  );
}

// ---------------------------------------------------------------------
// Visit timeline — the plan's spine, always visible
// ---------------------------------------------------------------------
// This replaces what used to be two separate tables showing the same
// visits: a "Visits" list with dates and doctors, and a "Payment
// schedule" table with per-visit money. Front desk read their three
// visits twice. One row now carries both, so "when is he coming back"
// and "what does he owe on that visit" are answered together.
//
// Mental model:
//   - Each plan phase = one planned visit (Visit 1, Visit 2, …).
//   - Phase number comes from the procedure_visits row itself, so a
//     bridge's prep can sit in Visit 1 and its cementation in Visit 2.
//   - Money per visit comes from buildTxPlanPaymentSchedule, matched by
//     position. No billing model set means no money column — the
//     schedule's own message says so rather than showing blank pesos.

// Build a procedure label from a procedure_visits row.
// Falls back to display_label/procedure_name for the row's parent
// item if those are joined onto the row (lifecycle v2 — drawer
// merges items into procedure_visits via item_id lookup).
function procedureLine(v) {
  const name = v.display_label || v.procedure_name || v.template_label || "Procedure";
  const tooth = v.tooth_number ? ` #${v.tooth_number}` : "";
  // Multi-visit procedures (Bridge prep / cementation) get a step
  // suffix so the receptionist can see which step of the procedure
  // this row represents.
  const step = v.template_label && name && v.template_label !== name
    ? ` · ${v.template_label}`
    : "";
  return `${name}${tooth}${step}`;
}

// Money-state chip. Vocabulary matches the payment schedule's own
// statuses, which classify on the rounded peso — so a chip can never
// read "Overdue" next to an amount that displays as ₱0.
const VISIT_CHIP = {
  paid:     { label: "Paid",     cls: "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200" },
  due_next: { label: "Due next", cls: "bg-amber-100 text-amber-900 ring-1 ring-amber-300" },
  overdue:  { label: "Overdue",  cls: "bg-rose-100 text-rose-800 ring-1 ring-rose-200" },
  no_due:   { label: "No fee",   cls: "bg-slate-100 text-slate-600 ring-1 ring-slate-200" },
};

const VISIT_AMOUNT_LABEL = {
  due_next: "due",
  overdue:  "overdue",
  upcoming: "later",
};

// Drop the year for dates inside the current year — "Jun 23" reads
// faster than "Jun 23, 26" and the year only matters for older visits.
function formatVisitDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const sameYear = d.getFullYear() === new Date().getFullYear();
  try {
    return d.toLocaleDateString(undefined, sameYear
      ? { month: "short", day: "numeric" }
      : { month: "short", day: "numeric", year: "2-digit" });
  } catch {
    return null;
  }
}

function VisitTimeline({ visits, items, schedule, currency, onAmendVisit }) {
  const phases = groupVisitPhases({ visits, items });
  const moneyRows = schedule?.rows || [];

  if (phases.length === 0) {
    return (
      <div className="px-4 py-3 bg-white border-b border-slate-200 text-[12px] text-slate-500 italic">
        No visits planned yet.
      </div>
    );
  }

  const doneCount = phases.filter((p) => p.allDone).length;

  return (
    <div className="bg-white border-b border-slate-200" data-testid="tx-plan-visits">
      <div className="px-4 pt-3 pb-1.5 flex items-baseline justify-between gap-2">
        <span className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">
          Visits
        </span>
        <span className="text-[11px] text-slate-500 tabular-nums">
          {doneCount} of {phases.length} done
        </span>
      </div>
      <ul>
        {phases.map((g, idx) => {
          const money = moneyRows[idx] || null;
          const status = money?.status || null;
          const chip = status ? VISIT_CHIP[status] : null;
          const dotClass = g.allDone ? "bg-emerald-500"
                        : g.anyDone  ? "bg-amber-500"
                        : g.anyBooked ? "bg-violet-500"
                        : "bg-slate-300";
          const rowTint = status === "overdue" ? "bg-rose-50/60"
                        : status === "due_next" ? "bg-amber-50/60"
                        : "";

          const when = g.anyBooked
            ? (formatVisitDate(g.date) || "booked")
            : "not booked";
          const names = g.rows.map(procedureLine);
          const shown = names.slice(0, 2).join(", ");
          const more = names.length > 2 ? ` +${names.length - 2} more` : "";
          const detail = [shown + more, when, g.doctor].filter(Boolean).join(" · ");

          // Amend affordance: only on sub-visits with a non-done outcome
          // already recorded (not_today, future_need, declined). The
          // "doctor forgot to mark V1 done" case from
          // REPORT_VISIT_CASCADE.md. Normal rows stay a single line.
          const amendable = onAmendVisit
            ? g.rows.filter((v) => v.outcome && v.outcome !== "done" && !v.completed_at)
            : [];

          return (
            <li
              key={g.phaseNum}
              className={["px-4 py-2 flex gap-2.5 border-t border-slate-100", rowTint].join(" ")}
            >
              <span className={`w-1.5 h-1.5 rounded-full shrink-0 mt-[7px] ${dotClass}`} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[12.5px] font-medium text-slate-900">
                    Visit {idx + 1}
                  </span>
                  {chip ? (
                    <span className={[
                      "px-1.5 py-0.5 rounded text-[9.5px] font-semibold uppercase tracking-wide",
                      chip.cls,
                    ].join(" ")}>
                      {chip.label}
                    </span>
                  ) : null}
                </div>
                <p className="mt-0.5 text-[11.5px] text-slate-500 truncate">{detail}</p>
                {amendable.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => onAmendVisit(v)}
                    data-testid={`amend-visit-${v.id}`}
                    className="mt-1 inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded"
                  >
                    Amend {procedureLine(v)}
                  </button>
                ))}
              </div>
              {money ? (
                <div className="text-right shrink-0">
                  <p className={[
                    "text-[12.5px] tabular-nums",
                    status === "overdue" ? "font-semibold text-rose-700"
                      : status === "due_next" ? "font-semibold text-slate-900"
                      : status === "paid" ? "text-slate-500"
                      : "text-slate-700",
                  ].join(" ")}>
                    {formatMoney(money.dueCentavos, currency)}
                  </p>
                  <p className="text-[10px] text-slate-400 tabular-nums">
                    {status === "paid"
                      ? `of ${formatMoney(money.plannedCentavos, currency)}`
                      : (VISIT_AMOUNT_LABEL[status] || "")}
                  </p>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
      {/* No billing model yet — say so here rather than leaving the money
          column silently blank. */}
      {schedule?.message ? (
        <p className="px-4 pt-1.5 pb-2.5 text-[11px] text-slate-500">{schedule.message}</p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------
// Payments folder — plan-level totals only
// ---------------------------------------------------------------------
// Per-visit amounts moved into the visit timeline, so this is now just
// the three plan totals plus the billing model that produced them. The
// progress bar is gone: it restated the same paid/total split printed
// directly above it.
function PlanMoneyPanel({ billingState, balance, plan, schedule, currency }) {
  const source = billingState || balance;
  if (!source) return null;
  const planStatus = String(plan?.status || source.plan_status || "").toLowerCase();
  const isProposed = planStatus === "proposed" || planStatus === "draft";
  const total = billingState
    ? Number(billingState.final_total_centavos ?? billingState.total_centavos ?? 0)
    : Number(balance.total_centavos ?? 0);
  const paid = Number(source.paid_centavos ?? 0);
  const remaining = billingState
    ? Number(billingState.remaining_centavos ?? 0)
    : Number(balance.balance_centavos ?? 0);
  const requiresAR = !!(balance?.requires_ar || (remaining > 0 && source.plan_status === "Completed"));
  const rowCount = (schedule?.rows || []).length;

  return (
    <div className="space-y-2">
      {requiresAR ? (
        <div className="flex">
          <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 ring-1 ring-amber-200">
            Requires AR
          </span>
        </div>
      ) : null}
      <dl className="text-[12px] space-y-1">
        <div className="flex items-baseline justify-between gap-2">
          <dt className="text-slate-500">{isProposed ? "Quoted total" : "Total"}</dt>
          <dd className="font-semibold tabular-nums text-slate-900">{formatMoney(total, currency)}</dd>
        </div>
        <div className="flex items-baseline justify-between gap-2">
          <dt className="text-slate-500">{isProposed ? "Collected" : "Paid"}</dt>
          <dd className="font-semibold tabular-nums text-emerald-700">{formatMoney(paid, currency)}</dd>
        </div>
        <div className="flex items-baseline justify-between gap-2 pt-1 border-t border-slate-100">
          <dt className="text-slate-500">{isProposed ? "Balance" : "Outstanding"}</dt>
          <dd className={[
            "font-semibold",
            isProposed ? "text-slate-600 text-[11px]" : "tabular-nums",
            !isProposed && remaining > 0 ? "text-rose-700" : "text-slate-700",
          ].join(" ")}>
            {isProposed ? "Starts after acceptance" : formatMoney(remaining, currency)}
          </dd>
        </div>
      </dl>
      {requiresAR && plan?.ar_reason ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-900">
          <span className="font-semibold">AR reason:</span> {plan.ar_reason}
        </div>
      ) : null}
      {/* Point at the visit timeline rather than repeating it here. */}
      <p className="text-[11px] text-slate-500" data-testid="tx-plan-billing-model">
        {schedule?.message
          ? schedule.message
          : `Billing model: ${schedule?.presetLabel || "—"}${
              rowCount > 0 ? ` across ${rowCount} visit${rowCount === 1 ? "" : "s"}` : ""
            }. Per-visit amounts are in the visit list above.`}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------
// History folder — patient facts, then the audit trail
// ---------------------------------------------------------------------
function ActivityLogPanel({ entries, patient }) {
  const list = entries || [];
  // The last three fields from the old Patient panel land here — patient
  // status, recall standing, and when we last saw them. They're background
  // facts, not answers, so they belong beside the history rather than at
  // the top of the screen.
  const facts = patient ? [
    patient.status ? `Patient: ${patient.status}` : null,
    patient.recall_status ? `Recall: ${patient.recall_status}` : null,
    patient.last_activity_at ? `Last seen ${formatRelative(patient.last_activity_at)}` : null,
  ].filter(Boolean) : [];
  // Show the four most recent and keep the rest one tap away. A plan
  // accumulates a lot of "Plan status reconciled to …" bookkeeping, and
  // fourteen of those buried the entries a person would actually read.
  // Nothing is hidden permanently — the full log is still here.
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? list : list.slice(0, 4);
  const hidden = list.length - visible.length;

  return (
    <div>
      {facts.length > 0 ? (
        <p className="mb-2 text-[11px] text-slate-500">{facts.join(" · ")}</p>
      ) : null}
      {list.length === 0 ? (
        <div className="text-[12px] text-slate-500 italic">No activity recorded yet.</div>
      ) : (
        <>
          <ul className={["space-y-1.5", showAll ? "max-h-[280px] overflow-y-auto" : ""].join(" ")}>
            {visible.map((a) => (
              <li key={a.id} className="text-[12px]">
                <div className="text-slate-900 leading-snug">{a.description}</div>
                <div className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-2">
                  <span>{formatRelative(a.occurred_at)}</span>
                  {a.user_name ? <><span>·</span><span>{a.user_name}</span></> : null}
                </div>
              </li>
            ))}
          </ul>
          {hidden > 0 ? (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              data-testid="tx-plan-activity-show-all"
              className="mt-2 px-2 py-1 rounded-md border border-slate-300 bg-white text-[11px] font-medium text-slate-700 hover:bg-slate-50"
            >
              Show all {list.length} entries
            </button>
          ) : null}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// Actions — one primary, the rest behind More
// ---------------------------------------------------------------------
// Same per-state action sets the footer has always produced; the only new
// thing is ranking. The first action of each state becomes the primary
// button under the answer sentence, everything else moves into the
// footer's More menu. That's what gets Mark Lost away from the button
// front desk presses fifty times a day.
//
// Pure function, no hooks — the drawer memoizes the result.
function buildDrawerActions({
  data,
  eligibleItem,
  eligibleCount = 0,
  onBookVisit,
  onMarkLost,
  onReconfirm,
  onMarkAccepted,
  onCollectAr,
  onPropose,
  onResendEmail,
  onDownloadPdf,
  onSignDocument,
  arBalanceCentavos = 0,
  proposalBusy = {},
  reconfirming,
  signatureDocument = null,
  signatureBusy = false,
}) {
  const stateKey = data?.pipeline_state;
  const planStatus = data?.plan?.status;
  const hasItems = (data?.plan?.items ?? []).length > 0;
  const canBook  = !!eligibleItem;
  const canCollectAr = arBalanceCentavos > 0 && (stateKey === "ar" || stateKey === "completed");
  const isSignedDocument = signatureDocument?.status === "signed" && !!signatureDocument?.signature;

  const bookButton = (label) => {
    // Suffix the count when 2+ items are waiting so the receptionist
    // knows the selection prompt is going to fire.
    const countSuffix = eligibleCount > 1 ? ` (${eligibleCount} procedures)` : "";
    return {
      key: "book",
      label: `${label}${countSuffix}`,
      Icon: CalendarPlus,
      onClick: canBook ? onBookVisit : undefined,
      disabled: !canBook,
      hint: canBook ? undefined : "No accepted items waiting to be scheduled",
      variant: "primary",
    };
  };

  const lostButton = {
    key: "lost",
    label: "Mark Lost",
    Icon: XCircle,
    onClick: onMarkLost,
    variant: "danger",
  };

  const reconfirmButton = {
    key: "reconfirm",
    label: reconfirming ? "Reconfirming…" : "Reconfirm",
    Icon: reconfirming ? Loader2 : RotateCw,
    iconSpin: reconfirming,
    onClick: reconfirming ? undefined : onReconfirm,
    disabled: reconfirming,
    variant: "primary",
  };

  const collectArButton = {
    key: "collect-ar",
    label: "Collect AR Payment",
    Icon: Wallet,
    onClick: canCollectAr ? onCollectAr : undefined,
    disabled: !canCollectAr,
    hint: canCollectAr ? undefined : "No AR balance to collect",
    variant: "primary",
  };

  const proposeButton = {
    key: "propose",
    label: proposalBusy.propose ? "Proposing..." : "Propose to patient",
    Icon: proposalBusy.propose ? Loader2 : Send,
    iconSpin: proposalBusy.propose,
    onClick: !proposalBusy.propose && hasItems ? onPropose : undefined,
    disabled: proposalBusy.propose || !hasItems,
    hint: hasItems ? "Email the plan with its PDF attached" : "Add at least one item before proposing",
    variant: "primary",
  };

  const resendButton = {
    key: "resend",
    label: proposalBusy.resend ? "Resending..." : "Resend email",
    Icon: proposalBusy.resend ? Loader2 : Send,
    iconSpin: proposalBusy.resend,
    onClick: !proposalBusy.resend && hasItems ? onResendEmail : undefined,
    disabled: proposalBusy.resend || !hasItems,
    hint: hasItems ? "Resend the plan email and PDF without changing status" : "No plan items to send",
    variant: "neutral",
  };

  const downloadPdfButton = {
    key: "download-pdf",
    label: proposalBusy.download ? "Saving..." : "Download PDF",
    Icon: proposalBusy.download ? Loader2 : Download,
    iconSpin: proposalBusy.download,
    onClick: !proposalBusy.download && hasItems ? onDownloadPdf : undefined,
    disabled: proposalBusy.download || !hasItems,
    hint: hasItems ? "Save the patient-facing treatment plan PDF" : "No plan items to save",
    variant: "neutral",
  };

  const signButton = isSignedDocument ? {
    key: "signed",
    label: "Signed",
    Icon: CheckCircle2,
    disabled: true,
    hint: "This document has a saved patient signature",
    variant: "signed",
  } : {
    key: "sign",
    label: signatureBusy ? "Signing..." : "Sign",
    Icon: signatureBusy ? Loader2 : PenLine,
    iconSpin: signatureBusy,
    onClick: !signatureBusy && hasItems ? onSignDocument : undefined,
    disabled: signatureBusy || !hasItems,
    hint: hasItems ? "Capture patient signature and accept this plan" : "No plan items to sign",
    variant: "sign",
  };

  // Per-state action set, most important first. Booking modal + Mark Lost
  // + Reconfirm are all inline in the drawer.
  const ranked = (() => {
    if (planStatus === "Draft" || stateKey === "draft") {
      return [proposeButton, downloadPdfButton];
    }
    switch (stateKey) {
      case "proposed":
        return [
          // Lifecycle v2: stay inside Tx Plan Center. The Mark
          // Accepted dialog hosts the two paths (book Visit 1 now /
          // queue for front-desk callback) without ever navigating
          // away to the patient or doctor surfaces.
          { key: "accept", label: "Mark Accepted", Icon: CheckCircle2, onClick: () => onMarkAccepted?.(), variant: "primary" },
          resendButton,
          downloadPdfButton,
          signButton,
          lostButton,
        ];
      case "expired":
        return [reconfirmButton, lostButton];
      case "needs_booking":
        // Lifecycle v2: patient said yes, no date yet → primary action
        // is to book Visit 1. Same affordance as the legacy 'accepted'.
        return [bookButton("Book Visit 1"), lostButton];
      case "scheduled":
        // Lifecycle v2: Visit 1 booked. Front desk can re-book if the
        // patient cancels (returns to Needs Booking via the chip flow).
        return [bookButton("Reschedule Visit 1"), lostButton];
      case "accepted":
        // Legacy fallback for any plans that came in pre-PR-1 with
        // pipeline_stage=NULL.
        return [bookButton("Book First Visit"), lostButton];
      case "in_progress":
        return [bookButton("Book Next Visit")];
      case "completed":
        return canCollectAr ? [collectArButton] : [];
      case "ar":
        return [collectArButton];
      case "paused":
      case "abandoned":
      case "rejected":
      case "superseded":
      default:
        return [];
    }
  })();

  return { primary: ranked[0] || null, secondary: ranked.slice(1) };
}

// ---------------------------------------------------------------------
// Action button
// ---------------------------------------------------------------------
function ActionButton({ action, full }) {
  const { label, Icon, onClick, disabled, hint, variant, iconSpin } = action;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={hint || undefined}
      data-testid={`tx-plan-action-${action.key}`}
      className={[
        "inline-flex items-center justify-center gap-1.5 rounded-md font-medium border",
        full ? "w-full px-3 py-2 text-[13px]" : "px-3 py-1.5 text-[12px]",
        disabled
          ? "border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed"
          : variant === "primary"
            ? "border-teal-600 bg-teal-600 text-white hover:bg-teal-700"
          : variant === "sign"
            ? "border-teal-200 bg-teal-50 text-teal-800 hover:bg-teal-100"
          : variant === "signed"
            ? "border-emerald-200 bg-emerald-50 text-emerald-800"
          : variant === "danger"
            ? "border-rose-300 bg-white text-rose-700 hover:bg-rose-50"
            : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50",
      ].join(" ")}
    >
      <Icon className={["w-3.5 h-3.5", iconSpin ? "animate-spin" : ""].join(" ")} strokeWidth={2.25} />
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------
// Footer: open-the-chart link + the More menu
// ---------------------------------------------------------------------
// Menu opens upward (the footer is pinned to the bottom of the sheet) and
// closes on Escape, on outside click, and after any item fires.
function DrawerFooter({ secondary = [], patientId, planId, isOperationalMv, onClose }) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setMenuOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  const goToPatientChart = () => {
    onClose?.();
    navigate(isOperationalMv
      ? `/patients/${patientId}`
      : `/patients/${patientId}?tab=treatment&plan=${planId}`);
  };

  return (
    <div className="px-4 py-2.5 border-t border-slate-200 bg-white flex items-center gap-2">
      <button
        type="button"
        onClick={goToPatientChart}
        className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md text-[12px] text-slate-700 hover:text-slate-900 hover:bg-slate-100"
      >
        {isOperationalMv ? "Open patient record" : "Open full chart"}
        <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2.25} />
      </button>
      <div className="flex-1" />
      {secondary.length > 0 ? (
        <div className="relative">
          {menuOpen ? (
            <div
              className="fixed inset-0 z-10"
              onClick={() => setMenuOpen(false)}
              aria-hidden="true"
            />
          ) : null}
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label="More actions"
            data-testid="tx-plan-more-actions"
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[12px] font-medium border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
          >
            <MoreHorizontal className="w-4 h-4" strokeWidth={2.25} />
            More
          </button>
          {menuOpen ? (
            <div
              role="menu"
              className="absolute z-20 bottom-full right-0 mb-1 w-56 rounded-md border border-slate-200 bg-white py-1 shadow-lg"
            >
              {secondary.map((action) => (
                <button
                  key={action.key}
                  type="button"
                  role="menuitem"
                  disabled={action.disabled}
                  title={action.hint || undefined}
                  data-testid={`tx-plan-action-${action.key}`}
                  onClick={() => {
                    setMenuOpen(false);
                    action.onClick?.();
                  }}
                  className={[
                    "w-full px-3 py-2 flex items-center gap-2 text-left text-[12px]",
                    action.disabled
                      ? "text-slate-400 cursor-not-allowed"
                      : action.variant === "danger"
                        ? "text-rose-700 hover:bg-rose-50"
                        : "text-slate-700 hover:bg-slate-50",
                  ].join(" ")}
                >
                  <action.Icon
                    className={["w-3.5 h-3.5 shrink-0", action.iconSpin ? "animate-spin" : ""].join(" ")}
                    strokeWidth={2.25}
                  />
                  {action.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------
